/**
 * Datumslogik für wiederkehrende Rechnungs-Vorlagen (M3b).
 *
 * ⚠️ Zeitzonen-Konsistenz: Diese Funktionen arbeiten AUSSCHLIESSLICH mit
 * `YYYY-MM-DD`-Strings und ganzzahliger Kalenderarithmetik — nie mit dem
 * Vergleich zweier `Date`-Objekte unterschiedlicher Konstruktion.
 *
 * Grund (Bugfix): Die frühere `computeNextRunAt`-Version mischte
 * `new Date(isoString)` (UTC-Mitternacht) mit `new Date(y, m, d)` (LOKALE
 * Mitternacht). In einer Zeitzone mit positivem Offset (Europe/Berlin, UTC+1/+2)
 * liegt die lokale Mitternacht des Tages D als Instant VOR der UTC-Mitternacht
 * desselben Tages → der Vergleich `candidate < today` war fälschlich `true`,
 * sobald der erste Lauftag = heute war, und die Vorlage sprang eine ganze
 * Periode weiter („Nächster Lauf 2026-08-18" statt heute).
 *
 * Da ISO-Datums-Strings lexikographisch = chronologisch sortieren, ist der
 * String-Vergleich zeitzonenfrei korrekt. Die Perioden-Fortschaltung spiegelt
 * die overflow-sichere Monatsarithmetik des serverseitigen
 * `RecurringPeriodService::computeNextRunAt()` (Referenz-Semantik).
 *
 * @module utils/recurringSchedule
 */

import { RecurringSchedule } from '../types/recurringTypes';

/**
 * Heutiges Datum als `YYYY-MM-DD` in der lokalen Zeitzone des Browsers.
 * Rein arithmetisch aus den lokalen Datumsteilen zusammengesetzt (kein
 * `toISOString()`, das nach UTC verschieben würde).
 */
export function getTodayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Anzahl Tage im Monat `month1` (1–12) des Jahres `year`. Nutzt `Date` nur zur
 * Ermittlung der Tageszahl (Tag 0 des Folgemonats) — das ist von der Zeitzone
 * unabhängig, da nur die Kalender-Komponente `getDate()` gelesen wird.
 */
export function daysInMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

/** Formatiert (Jahr, Monat 1–12, Tag) als `YYYY-MM-DD`. */
function formatISO(year: number, month1: number, day: number): string {
  const mm = String(month1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * Berechnet den initialen `nextRunAt` (erster fälliger Lauf) für einen Schedule.
 *
 * Semantik: Kandidat ist `dayOfMonth` (auf die Monatslänge geklammert) im
 * Startmonat. Liegt dieser Kandidat vor heute, wird um genau eine Periode
 * (monatlich/vierteljährlich/jährlich) fortgeschaltet; andernfalls ist der
 * Kandidat selbst der nächste Lauf. „Erster Lauf = heute" ergibt korrekt HEUTE.
 *
 * @param schedule Schedule-Konfiguration der Vorlage.
 * @param todayISO Vergleichs-Heute als `YYYY-MM-DD` (Default: {@link getTodayISO}).
 *                 Injizierbar für deterministische Tests.
 * @returns Nächstes Lauf-Datum als `YYYY-MM-DD` oder `null`, wenn kein gültiges
 *          `startDate` vorliegt.
 */
export function computeNextRunAt(
  schedule: RecurringSchedule,
  todayISO: string = getTodayISO()
): string | null {
  if (!schedule.startDate) return null;

  const [sy, sm] = schedule.startDate.split('-').map((p) => parseInt(p, 10));
  if (!Number.isInteger(sy) || !Number.isInteger(sm) || sm < 1 || sm > 12) {
    return null;
  }

  const clampDay = (year: number, month1: number): number =>
    Math.min(schedule.dayOfMonth, daysInMonth(year, month1));

  // Kandidat im Startmonat.
  const candidateISO = formatISO(sy, sm, clampDay(sy, sm));

  // ISO-Strings sortieren lexikographisch = chronologisch → zeitzonenfrei.
  if (candidateISO >= todayISO) {
    return candidateISO;
  }

  // Um eine Periode fortschalten (overflow-sicher, spiegelt RecurringPeriodService).
  let ty = sy;
  let tm = sm;
  if (schedule.interval === 'monthly') {
    tm += 1;
    if (tm > 12) {
      tm = 1;
      ty += 1;
    }
  } else if (schedule.interval === 'quarterly') {
    tm += 3;
    if (tm > 12) {
      tm -= 12;
      ty += 1;
    }
  } else {
    // yearly
    ty += 1;
  }

  return formatISO(ty, tm, clampDay(ty, tm));
}
